import { useEffect, useState } from "react";
import { useModal, useToast } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";
import type { AssignRoleRequest } from "common/models/api/models/AssignRoleRequest";
import type { BasicUserDto } from "common/models/api/models/BasicUserDto";
import type { PaginatedUsersDto } from "common/models/api/models/PaginatedUsersDto";
import useUsersFindAll from "features/users/hooks/useUsersFindAll";
import useAssignRole from "features/roles/hooks/useAssignRole";
import useUserState from "ui/adapter/state/useUserState";
import { CreateAdminDTO, createAdminSchema } from "../../schemas/CreateAdminSchema";
import CreateAdminModal from "../CreateAdminModal";
import useVerifyReleaseStrategyMethod from "ui/release-strategy/hooks/useVerifyReleaseStrategyMethod";
import { RoleType } from "common/models/role/Role";
import { Operations } from "ui/admins/operations.types";
import { AssignRoleRequestDto } from "ui/admins/shared/dto/assign-role-request.dto";
import { useQueryClient } from "react-query";
import { Queries } from "ui/query/queries";

interface UseCreateAdminModalProps {
    admin: BasicUserDto;
}

export default function useCreateAdminModal({ admin }: UseCreateAdminModalProps) {
    const translate = useTranslate();
    const translateError = useTranslate("error");
    const { hideModal } = useModal();
    const { selectedRole } = useUserState();
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const [isValidForm, setIsValidForm] = useState<boolean>(false);
    const [form, setForm] = useState<CreateAdminDTO>({
        adminPermissions: RoleType.OPERATION_MANAGER,
        identificationType: admin?.identificationType || "",
        identificationCode: admin?.identificationCode || "",
        name: admin?.name || "",
        phone: admin?.phone || "",
        email: admin?.email || "",
        userId: undefined,
    });

    const handleFormChange = (field: keyof CreateAdminDTO, value: string | number) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    function closeModal() {
        hideModal(CreateAdminModal.id);
    }

    function handleAssignmentSuccess() {
        queryClient.invalidateQueries(Queries.RELEASE_STRATEGY_FIND_ALL);
        showToast(translate("infoSavedSuccesfully"), { type: "success" });
        closeModal();
    }

    function handleUsersRequestSuccess(data: PaginatedUsersDto) {
        if (data.items.length) {
            const user = data.items[0];
            handleFormChange("name", user.name);
            handleFormChange("phone", user.phone);
            handleFormChange("email", user.email);
            handleFormChange("userId", user.id);
        } else {
            showToast(translateError("userNotFound"), { type: "error" });
        }
    }

    const { mutate: assignRole, isLoading: isAssigningRole } = useAssignRole({ onSuccess: handleAssignmentSuccess });
    const { mutate: updateRole, isLoading: isUpdatingRole } = useAssignRole({ onSuccess: handleAssignmentSuccess });
    const { refetch, isLoading: isFetchingUserData } = useUsersFindAll(
        { query: form.identificationCode },
        { enabled: false, onSuccess: handleUsersRequestSuccess },
    );

    const { verifyReleaseMethod, isValidatingReleaseStrategy } = useVerifyReleaseStrategyMethod({
        onSuccess: handleAssignmentSuccess,
        isLoading: isAssigningRole,
    });

    useEffect(() => {
        const result = createAdminSchema.safeParse(form);
        setIsValidForm(result.success);
    }, [form]);

    async function handleSubmit() { // skipcq: JS-0116
        const result = createAdminSchema.safeParse(form);
        if (result.error) return;
        const requestBody = mapFormToDtoRequest(form);
        const executeAction = (...args: unknown[]) => {
            const [body] = args as [AssignRoleRequest];
            if (admin) {
                assignRole(body);
            } else {
                updateRole(body);
            }
        };

        verifyReleaseMethod({
            originalFn: executeAction,
            originalArgs: [requestBody],
            operationTypeId:
                requestBody.roleType === RoleType.CUSTOMER_SERVICE
                    ? Operations.CREATE_OR_MODIFY_CUSTOMER_SERVICE_ADMIN
                    : Operations.CREATE_OR_MODIFY_OPERATION_MANAGER_ADMIN,
        });
    }

    function mapFormToDtoRequest(form: CreateAdminDTO): AssignRoleRequest {
        const payload = {
            userId: form.userId as number,
            roleType: form.adminPermissions as RoleType,
            organizationAddress: selectedRole?.organization?.address as string,
        };
        const assignRoleRequest = AssignRoleRequestDto.create(payload);
        return assignRoleRequest;
    }

    function handleIdentificationInputBlur() {
        if (form.identificationCode) {
            refetch();
        }
    }

    return {
        form,
        isValidForm,
        isValidatingReleaseStrategy,
        isSendingData: isAssigningRole || isUpdatingRole || isValidatingReleaseStrategy,
        isFetchingUserData,
        handleFormChange,
        handleSubmit,
        handleIdentificationInputBlur,
        closeModal,
    };
}
